// ---------------------------------------------------------------------------
// Los importes no salen de la consola (#1349).
//
// La API solo manda dinero (costos, sueldos, pérdidas) a los dueños, pero la
// pantalla del dueño la ven también las herramientas de telemetría: la
// repetición de sesiones de Sentry graba el DOM, y el autocapture de PostHog
// manda el texto del botón que se pulsa. Un renglón «Desarrollo 12.5 h
// $4,500.00 MXN» de un tipo con una sola persona es su sueldo por hora.
//
// Dos defensas, las dos necesarias:
//   • `MONEY_PRIVACY_CLASS` en cada elemento que pinta un importe: PostHog no
//     captura ni graba lo marcado `ph-no-capture`, y Sentry enmascara lo marcado
//     `sentry-mask`. Va en el elemento del importe, NO en la tarjeta entera:
//     PostHog descarta todo clic dentro de un elemento `ph-no-capture`.
//   • `sanitizeCapturedProps` sobre lo que aun así se capture (un importe
//     pintado por un componente que nadie marcó).
// ---------------------------------------------------------------------------

/** Clases que excluyen un elemento de PostHog (captura y grabación) y lo enmascaran en Sentry. */
export const MONEY_PRIVACY_CLASS = "ph-no-capture sentry-mask";

export const MONEY_REDACTED = "[importe]";

/** Texto de un elemento que se deja salir: suficiente para identificar el control, no para exportar datos. */
export const CAPTURED_TEXT_MAX = 60;

// Número con separadores de miles (coma, punto o espacio, también el espacio
// fino/duro que pone `Intl.NumberFormat`) y decimales.
const NUM = String.raw`(?:\d{1,3}(?:[.,\u00a0\u202f ]\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)?)`;
const CODE = String.raw`(?:MXN|USD|EUR|CAD|GBP|JPY|CHF|COP|ARS|BRL|CLP|PEN|GTQ|CNY)`;
// «$4,500.00», «US$50», «-$1,200 MXN», «¥1,200 JPY», «4 500,00 MXN», «CHF 1,200.00»…
const MONEY_PATTERN = new RegExp(
  [String.raw`[A-Z]{0,3}\p{Sc}[\s\u00a0\u202f]?${NUM}(?:[\s\u00a0\u202f]?${CODE}\b)?`, String.raw`${NUM}[\s\u00a0\u202f]?${CODE}\b`, String.raw`\b${CODE}[\s\u00a0\u202f]${NUM}`].join("|"),
  "gu",
);

/** Quita los importes de un texto que va a salir de la consola (telemetría). */
export const redactMoney = (text: string): string => text.replace(MONEY_PATTERN, MONEY_REDACTED);

const clean = (text: string): string => redactMoney(text).slice(0, CAPTURED_TEXT_MAX);

/**
 * Atributos del DOM que llevan texto escrito por una persona (un nombre, una
 * placa, un folio) y que PostHog copia tal cual porque no se enmascaran los
 * atributos. `href` queda fuera a propósito: PostHog lo usa para emparejar
 * acciones, así que cortarlo rompería esa función en silencio.
 */
const TEXT_ATTRIBUTES = ["attr__title", "attr__aria-label", "attr__alt", "attr__placeholder"] as const;

/**
 * `clean` para un valor YA escapado de `$elements_chain`.
 *
 * Ahí las comillas van como `\"` (dos caracteres). Un corte a secas puede partir
 * esa pareja y dejar una barra suelta al final, que escapa la comilla de cierre:
 * la cadena se traga los atributos siguientes y el recorte deja de valer justo
 * en el caso que venía a cubrir. Si el corte termina en un número impar de
 * barras, se quita la última.
 */
const cleanEscaped = (value: string): string => {
  let cut = clean(value);
  let slashes = 0;
  while (slashes < cut.length && cut[cut.length - 1 - slashes] === "\\") slashes += 1;
  if (slashes % 2 === 1) cut = cut.slice(0, -1);
  return cut;
};

const CHAIN_TEXT = new RegExp(`(text|${TEXT_ATTRIBUTES.join("|")})="((?:[^"\\\\]|\\\\.)*)"`, "g");

/**
 * Limpia las propiedades de un evento de autocapture ANTES de enviarlo: recorta
 * el texto a `CAPTURED_TEXT_MAX` y quita los importes.
 *
 * PostHog manda el texto del elemento en tres sitios y hay que cubrir los tres:
 * `$elements[].$el_text`, `$el_text` (nivel superior) y `$elements_chain` (una
 * cadena con `text="…"` por elemento; según la configuración remota es la única
 * que viaja). Limpiar solo `$elements` no protegía nada.
 *
 * Y no sólo el texto: `title`, `aria-label`, `alt` y `placeholder` viajan en los
 * mismos tres sitios, así que un `<a title="Juan Pérez - ABC-123">` salía entero
 * aunque su texto fuera corto.
 */
// Prellenado del registro (`/register?name=…&email=…&phone=…&company=…`,
// CC-CONSOLE#1538): datos de una persona que no deben salir en la URL de un
// evento ni en el `href` de un enlace capturado. Se tacha el VALOR y se deja
// el parámetro (la estructura de la URL sigue sirviendo para emparejar acciones).
// `intent` (qué quiere probar) y `ph_did`/`ph_sid` (su identidad en PostHog) también.
const PII_PARAM = /([?&#]|&amp;)(name|email|phone|company|nombre|correo|telefono|empresa|intent|ph_did|ph_sid)=[^&#"'\s<>]*/gi;
export const PII_REDACTED = "[redactado]";

/** Tacha los parámetros personales de una URL (o de un texto que la contiene). */
export const redactUrlPii = (text: string): string =>
  text.replace(PII_PARAM, (_m, sep: string, key: string) => `${sep}${key}=${PII_REDACTED}`);

// Propiedades de PostHog que llevan una URL.
const URL_PROPS = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer", "$prev_pageview_pathname", "$pathname"] as const;

export function sanitizeCapturedProps(props: Record<string, unknown>): void {
  for (const key of URL_PROPS) {
    if (typeof props[key] === "string") props[key] = redactUrlPii(props[key] as string);
  }
  const elements = props.$elements;
  if (Array.isArray(elements)) {
    for (const el of elements as Record<string, unknown>[]) {
      if (!el) continue;
      if (typeof el.$el_text === "string") el.$el_text = clean(el.$el_text);
      if (typeof el.attr__href === "string") el.attr__href = redactUrlPii(el.attr__href);
      for (const key of TEXT_ATTRIBUTES) {
        if (typeof el[key] === "string") el[key] = clean(el[key] as string);
      }
    }
  }
  if (typeof props.$el_text === "string") props.$el_text = clean(props.$el_text);
  if (typeof props.$elements_chain === "string") {
    // Cada `text="…"` y cada atributo con texto se limpia por separado; el
    // resto de la cadena son etiquetas, clases y atributos sin texto libre.
    props.$elements_chain = redactUrlPii(
      props.$elements_chain.replace(
        CHAIN_TEXT,
        (_m, key: string, value: string) => `${key}="${cleanEscaped(value)}"`,
      ),
    );
  }
}
